import { StandardEvents } from '@shopify/events';

/**
 * Keeps Dowelmax's native-radio product gallery aligned with Shopify variants.
 * Curated theme assets and audited product-attached media match exact option
 * values; ordinary Shopify featured media match variant IDs from Product Admin.
 */
class DmProductGallery extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const eventTarget = this.closest('.shopify-section, dialog');
    eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {import('@shopify/theme-events').ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (event.product?.handle && event.product.handle !== this.dataset.productHandle) return;

    this.#selectVariant(event.detail?.variantId, event.selectedOptions);

    event.promise
      .then(({ variant }) => {
        if (!variant) return;
        this.#selectVariant(variant.id, variant.selectedOptions);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[dm-product-gallery] Variant resolution failed:', error);
      });
  };

  /**
   * @param {string | number | undefined} variantId
   * @param {{ value: string }[] | undefined} selectedOptions
   */
  #selectVariant(variantId, selectedOptions) {
    const variantKey = variantId == null ? '' : String(variantId).split('/').pop();
    const optionValues = new Set((selectedOptions ?? []).map(({ value }) => value));
    const mappedInputs = Array.from(
      this.querySelectorAll('.dm-pgal__input[data-variant-ids], .dm-pgal__input[data-variant-values]')
    );

    if (mappedInputs.length === 0) return;

    const matchingInput = mappedInputs.find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;

      const variantIds = (input.dataset.variantIds ?? '').split('^').filter(Boolean);
      if (variantKey && variantIds.includes(variantKey)) return true;

      const variantValues = (input.dataset.variantValues ?? '').split('^').filter(Boolean);
      return variantValues.some((value) => optionValues.has(value));
    });

    const targetInput = matchingInput ?? this.querySelector('.dm-pgal__input[data-gallery-default]');
    if (!(targetInput instanceof HTMLInputElement) || targetInput.checked) return;

    targetInput.checked = true;
    targetInput.dispatchEvent(new Event('change', { bubbles: true }));

    const thumbnail = targetInput.parentElement?.querySelector('.dm-pgal__thumb');
    thumbnail?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

if (!customElements.get('dm-product-gallery')) {
  customElements.define('dm-product-gallery', DmProductGallery);
}
